import type { BallColor } from '@/components/lottery-ball-row'

// ==================== 常量表 ====================

/** 生肖对肖表：[生肖, 对冲生肖, 代码] */
const ZODIAC_TABLE: [string, string, string][] = [
  ['猴', '虎', 'sxhou'],
  ['羊', '牛', 'sxyang'],
  ['马', '鼠', 'sxma'],
  ['蛇', '兔', 'sxshe'],
  ['龙', '狗', 'sxlong'],
  ['兔', '鸡', 'sxtu'],
  ['虎', '猴', 'sxhu'],
  ['牛', '羊', 'sxniu'],
  ['鼠', '马', 'sxshu'],
  ['猪', '蛇', 'sxzhu'],
  ['狗', '龙', 'sxgou'],
  ['鸡', '兔', 'sxji'],
]

/** 波色表：红、蓝、绿 */
const COLOR_TABLE: [BallColor, number[]][] = [
  ['red', [1, 2, 7, 8, 12, 13, 18, 19, 23, 24, 29, 30, 34, 35, 40, 45, 46]],
  ['blue', [3, 4, 9, 10, 14, 15, 20, 25, 26, 31, 36, 37, 41, 42, 47, 48]],
  ['green', [5, 6, 11, 16, 17, 21, 22, 27, 28, 32, 33, 38, 39, 43, 44, 49]],
]

/** 号码 → 波色 快速查找表 */
const COLOR_MAP: Record<number, BallColor> = (() => {
  const map: Record<number, BallColor> = {}
  for (const [color, numbers] of COLOR_TABLE) {
    for (const n of numbers) map[n] = color
  }
  return map
})()

/** 五行表（30 种纳音五行循环） */
const WUXING_TABLE = [
  '金',
  '金',
  '火',
  '火',
  '木',
  '木',
  '土',
  '土',
  '金',
  '金',
  '火',
  '火',
  '水',
  '水',
  '土',
  '土',
  '金',
  '金',
  '木',
  '木',
  '水',
  '水',
  '土',
  '土',
  '火',
  '火',
  '木',
  '木',
  '水',
  '水',
] as const

/** 农历春节公历日期（月-日），用于判断生肖年份 */
const LUNAR_NEW_YEAR: Record<number, [number, number]> = {
  1976: [1, 31],
  1977: [2, 18],
  1978: [2, 7],
  1979: [1, 28],
  1980: [2, 16],
  1981: [2, 5],
  1982: [1, 25],
  1983: [2, 13],
  1984: [2, 2],
  1985: [2, 20],
  1986: [2, 9],
  1987: [1, 29],
  1988: [2, 17],
  1989: [2, 6],
  1990: [1, 27],
  1991: [2, 15],
  1992: [2, 4],
  1993: [1, 23],
  1994: [2, 10],
  1995: [1, 31],
  1996: [2, 19],
  1997: [2, 7],
  1998: [1, 28],
  1999: [2, 16],
  2000: [2, 5],
  2001: [1, 24],
  2002: [2, 12],
  2003: [2, 1],
  2004: [1, 22],
  2005: [2, 9],
  2006: [1, 29],
  2007: [2, 18],
  2008: [2, 7],
  2009: [1, 26],
  2010: [2, 14],
  2011: [2, 3],
  2012: [1, 23],
  2013: [2, 10],
  2014: [1, 31],
  2015: [2, 19],
  2016: [2, 8],
  2017: [1, 28],
  2018: [2, 16],
  2019: [2, 5],
  2020: [1, 25],
  2021: [2, 12],
  2022: [2, 1],
  2023: [1, 22],
  2024: [2, 10],
  2025: [1, 29],
  2026: [2, 17],
  2027: [2, 6],
  2028: [1, 26],
  2029: [2, 13],
  2030: [2, 3],
}

// ==================== 核心方法 ====================

/**
 * 根据公历日期判断所属的生肖年份。
 * 春节前属于上一年生肖，春节当天及之后属于当年生肖。
 */
export function getZodiacYear(date: Date): number {
  const year = date.getFullYear()
  const entry = LUNAR_NEW_YEAR[year]
  if (!entry) return year

  const [month, day] = entry
  // 月份从 0 开始
  const springDate = new Date(year, month - 1, day)
  return date < springDate ? year - 1 : year
}

/**
 * 获取当天日期字符串（YYYY-MM-DD），用于直播场景
 */
export function getTodayDateStr(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * 解析开奖日期字符串（支持 "2025年01月02日" 和 "2025-01-02" 两种格式）
 */
export function parseLotteryDate(dateStr: string): Date {
  const normalized = dateStr.replace(/[年月]/g, '-').replace(/日/g, '')
  return new Date(normalized)
}

/**
 * 获取号码对应的生肖
 */
export function getZodiac(zodiacYear: number, num: number): string {
  let index = (2016 - zodiacYear + num + 23) % 12
  if (index < 0) index += 12
  return ZODIAC_TABLE[index]![0]
}

/**
 * 获取号码对应的生肖代码（如 "sxhou"）
 */
export function getZodiacCode(zodiacYear: number, num: number): string {
  let index = (2016 - zodiacYear + num + 23) % 12
  if (index < 0) index += 12
  return ZODIAC_TABLE[index]![2]
}

/**
 * 获取号码对应的波色
 */
export function getBallColor(num: number): BallColor {
  return COLOR_MAP[num] ?? 'red'
}

/**
 * 获取号码对应的五行
 */
export function getWuxing(zodiacYear: number, num: number): string {
  let index = (120 + zodiacYear - 1983 - num) % 30
  if (index < 0) index += 30
  return WUXING_TABLE[index]!
}

// ==================== 组合接口 ====================

export interface BallAttributes {
  color: BallColor
  zodiac: string
  wuxing: string
}

/**
 * 获取单个号码的完整属性（波色、生肖、五行）
 *
 * @param num 号码 (1-49)
 * @param zodiacYear 生肖年份（已通过 getZodiacYear 计算）
 */
export function getBallAttributes(num: number, zodiacYear: number): BallAttributes {
  return {
    color: getBallColor(num),
    zodiac: getZodiac(zodiacYear, num),
    wuxing: getWuxing(zodiacYear, num),
  }
}

/**
 * 批量获取一期开奖号码的完整属性
 *
 * @param numbers 号码数组 (1-49)
 * @param dateStr 开奖日期字符串（如 "2025年01月02日" 或 "2025-01-02"）
 */
export function getBallAttributesByDate(numbers: number[], dateStr: string): BallAttributes[] {
  const date = parseLotteryDate(dateStr)
  const zodiacYear = getZodiacYear(date)
  return numbers.map((num) => getBallAttributes(num, zodiacYear))
}

/**
 * 把"短期号"补全为"年份4位+3位"格式(如港彩 "026" → "2026026")。
 * 已经是 7 位以上的直接返回。年份优先取 referenceDate 的前 4 位
 * (格式如 "2026/04/27"),fallback 到当前年。
 */
export function normalizeIssue(issue?: string | null, referenceDate?: string | null): string {
  if (!issue) return ''
  if (issue.length >= 7) return issue
  let year: string
  if (referenceDate && /^(\d{4})/.test(referenceDate)) {
    year = referenceDate.slice(0, 4)
  } else {
    year = String(new Date().getFullYear())
  }
  return year + issue.padStart(3, '0')
}
